import React, { useMemo, useState } from "react";
import {
    Box,
    Container,
    SimpleGrid,
    GridItem,
    FormControl,
    FormLabel,
    Switch,
    InputGroup,
    InputRightAddon,
    Input,
    Text,
    Stat,
    StatLabel,
    StatNumber,
    useColorModeValue,
    Badge,
    HStack,
    Icon,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Tag,
    TagLeftIcon,
    TagLabel,
    StatHelpText,
} from "@chakra-ui/react";
import {
    FiPercent,
    FiTrendingUp,
    FiTarget,
    FiLayers,
    FiDollarSign,
    FiGrid,
} from "react-icons/fi";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";

const parseLocaleNumber = (value) => {
    if (typeof value === "number") return value;
    if (value == null) return 0;

    const raw = String(value).trim();
    if (!raw) return 0;

    const hasDot = raw.includes(".");
    const hasComma = raw.includes(",");

    let normalized = raw;

    if (hasDot && hasComma) {
        normalized = raw.replace(/\./g, "").replace(",", ".");
    } else if (hasComma && !hasDot) {
        normalized = raw.replace(",", ".");
    }

    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
};

const formatPercentDelta = (value) =>
    `${(Number.isFinite(value) ? value : 0).toFixed(2).replace(".", ",")}%`;

const formatRON = (v, digits = 2) => {
    const s = (Number.isFinite(v) ? v : 0).toFixed(digits);
    const [intPart, dec] = s.split(".");
    const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${intFmt},${dec} RON`;
};

const formatPercent2 = (v) =>
    `${(Number.isFinite(v) ? v : 0).toFixed(2).replace(".", ",")}%`;

/* ---------------- Reusable inputs ---------------- */
const MoneyField = ({ label, hint, value, onChange }) => (
    <FormControl>
        <FormLabel fontWeight="600">
            {label}{" "}
            {hint ? (
                <Text as="span" color="gray.500" fontSize="sm">
                    ({hint})
                </Text>
            ) : null}
        </FormLabel>
        <InputGroup size="lg">
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="0"
                inputMode="decimal"
                borderRadius="xl"
                bg="whiteAlpha.700"
                _dark={{ bg: "whiteAlpha.100" }}
                backdropFilter="auto"
                backdropBlur="8px"
            />
            <InputRightAddon
                borderTopRightRadius="xl"
                borderBottomRightRadius="xl"
                borderLeftRadius="0"
            >
                RON
            </InputRightAddon>
        </InputGroup>
    </FormControl>
);

const NumberField = ({ label, hint, value, onChange }) => (
    <FormControl>
        <FormLabel fontWeight="600">
            {label}{" "}
            {hint ? (
                <Text as="span" color="gray.500" fontSize="sm">
                    ({hint})
                </Text>
            ) : null}
        </FormLabel>
        <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="0"
            inputMode="decimal"
            size="lg"
            borderRadius="xl"
            bg="whiteAlpha.700"
            _dark={{ bg: "whiteAlpha.100" }}
            backdropFilter="auto"
            backdropBlur="8px"
        />
    </FormControl>
);

/* ---------------- KPI Card ---------------- */
function KpiStatCard({ icon, label, value, accent = "teal", statusTag }) {
    const cardBg = useColorModeValue("white", "gray.800");
    const cardBorder = useColorModeValue("blackAlpha.200", "whiteAlpha.200");

    return (
        <Box
            bg={cardBg}
            p={{ base: 5, md: 6 }}
            borderRadius="2xl"
            border="1px solid"
            borderColor={cardBorder}
            boxShadow="md"
            _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
            transition="all .2s ease"
        >
            <HStack spacing={3} mb={2}>
                <Icon as={icon} boxSize={5} color={`${accent}.400`} />
                <Badge colorScheme={accent} variant="subtle" borderRadius="full">
                    {label}
                </Badge>
            </HStack>

            <Stat>
                <StatLabel color="gray.500">{label}</StatLabel>
                <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>{value}</StatNumber>
            </Stat>

            {statusTag ? (
                <Tag
                    mt={3}
                    size="lg"
                    colorScheme={statusTag.colorScheme}
                    variant="subtle"
                    borderRadius="full"
                    w="fit-content"
                >
                    {statusTag.icon ? <TagLeftIcon as={statusTag.icon} /> : null}
                    <TagLabel>{statusTag.text}</TagLabel>
                </Tag>
            ) : null}
        </Box>
    );
}

/* =====================================================================================
   ROI & Break-even calculator
===================================================================================== */
function RoiBreakEvenCalculator() {
    const { i18n } = useTranslation();
    const l = (en, ro) => (i18n.language === "ro" ? ro : en);
    const [isVatRegistered, setIsVatRegistered] = useState(true);
    const [vatRatePercent, setVatRatePercent] = useState("21");
    const [returnRatePercent, setReturnRatePercent] = useState("0");

    const [productCostExVat, setProductCostExVat] = useState("0");
    const [shippingCostExVat, setShippingCostExVat] = useState("0");
    const [packagingCostExVat, setPackagingCostExVat] = useState("0");

    const [targetRoas, setTargetRoas] = useState("3,5");
    const [sellingPriceInclVat, setSellingPriceInclVat] = useState("0");

    const inputsCardBg = useColorModeValue("whiteAlpha.800", "whiteAlpha.50");
    const inputsCardBorder = useColorModeValue("blackAlpha.200", "whiteAlpha.200");

    const computed = useMemo(() => {
        const vatRate = parseLocaleNumber(vatRatePercent);
        const returnRate = parseLocaleNumber(returnRatePercent);

        const productCost = parseLocaleNumber(productCostExVat);
        const shippingCost = parseLocaleNumber(shippingCostExVat);
        const packagingCost = parseLocaleNumber(packagingCostExVat);

        const roasTarget = parseLocaleNumber(targetRoas);
        const grossSellingPrice = parseLocaleNumber(sellingPriceInclVat);

        const costPerResult = roasTarget > 0 ? grossSellingPrice / roasTarget : 0;

        const netSellingPriceAfterReturns = (grossSellingPrice * (100 - returnRate)) / 100;

        const vatCollectedOnSales =
            netSellingPriceAfterReturns - netSellingPriceAfterReturns / (1 + vatRate / 100);

        const vatPaidOnCosts = (shippingCost + packagingCost + productCost) * (vatRate / 100);

        const vatNetPayable = isVatRegistered ? vatCollectedOnSales - vatPaidOnCosts : vatPaidOnCosts;

        const orderProfit =
            netSellingPriceAfterReturns -
            shippingCost -
            packagingCost -
            productCost -
            costPerResult -
            vatNetPayable;

        const profitMarginPercent =
            netSellingPriceAfterReturns > 0 ? (orderProfit / netSellingPriceAfterReturns) * 100 : 0;

        const breakEvenDenominator = costPerResult + orderProfit;
        const breakEvenPoint = breakEvenDenominator !== 0 ? grossSellingPrice / breakEvenDenominator : 0;

        const roiPercent = productCost > 0 ? (orderProfit / productCost) * 100 : 0;

        const netSellingPriceNoReturns = grossSellingPrice;
        const vatCollectedNoReturns =
            netSellingPriceNoReturns - netSellingPriceNoReturns / (1 + vatRate / 100);

        const vatNetPayableNoReturns = isVatRegistered
            ? vatCollectedNoReturns - vatPaidOnCosts
            : vatPaidOnCosts;

        const profitNoReturns =
            netSellingPriceNoReturns - shippingCost - packagingCost - productCost - costPerResult - vatNetPayableNoReturns;

        const roiBeforeReturnsPercent = productCost > 0 ? (profitNoReturns / productCost) * 100 : 0;

        const vatCostForChart = Math.max(vatNetPayable, 0);
        const totalCostsForChart = Math.max(
            shippingCost + packagingCost + productCost + costPerResult + vatCostForChart,
            0.0001
        );

        const costItems = [
            { label: "Product", value: productCost },
            { label: "Shipping", value: shippingCost },
            { label: "Packing", value: packagingCost },
            { label: "Ads (CPR)", value: costPerResult },
            { label: "VAT to Pay", value: vatCostForChart },
        ];

        const costSharePercentages = costItems.map((item) => {
            const pct = (item.value / totalCostsForChart) * 100;
            return Number.isFinite(pct) ? Math.round(pct * 10) / 10 : 0;
        });

        const costBreakdownChart = {
            labels: costItems.map((i) => i.label),
            series: costSharePercentages,
        };

        let profitabilityStatus = { color: "green", text: "Profitable" };
        if (orderProfit <= 0) profitabilityStatus = { color: "red", text: "Unprofitable" };
        else if (profitMarginPercent >= 0 && profitMarginPercent < 10) {
            profitabilityStatus = { color: "yellow", text: "Needs Optimization" };
        }

        const roiImpactPercent = roiBeforeReturnsPercent - roiPercent;

        return {
            display: {
                orderProfit: formatRON(orderProfit),
                roi: formatPercent2(roiPercent),
                profitMargin: formatPercent2(profitMarginPercent),
                breakEvenPoint: breakEvenPoint.toFixed(2).replace(".", ","),
                costPerResult: formatRON(costPerResult),
                roiBeforeReturns: formatPercent2(roiBeforeReturnsPercent),
                roiAfterReturns: formatPercent2(roiPercent),
                roiImpact: formatPercentDelta(roiImpactPercent),
            },
            profitabilityStatus,
            costBreakdownChart,
        };
    }, [
        isVatRegistered,
        vatRatePercent,
        returnRatePercent,
        productCostExVat,
        shippingCostExVat,
        packagingCostExVat,
        targetRoas,
        sellingPriceInclVat,
    ]);

    return (
        <CalculatorShell>
            <InputsCard surface={inputsCardBg} borderCol={inputsCardBorder}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <GridItem>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0" fontWeight="700">
                                {l("VAT Payer?", "Platitor TVA?")}
                            </FormLabel>
                            <Switch
                                isChecked={isVatRegistered}
                                onChange={(e) => setIsVatRegistered(e.target.checked)}
                                colorScheme="teal"
                                size="lg"
                            />
                        </FormControl>
                    </GridItem>

                    <GridItem>
                        <NumberField label={l("Return Rate %", "Rata retur %")} value={returnRatePercent} onChange={setReturnRatePercent} />
                    </GridItem>

                    <GridItem>
                        <NumberField label="TVA %" value={vatRatePercent} onChange={setVatRatePercent} />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Import Price", "Pret import")}
                            hint={l("without VAT", "fara TVA")}
                            value={productCostExVat}
                            onChange={setProductCostExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Shipping Price", "Pret transport")}
                            hint={l("without VAT", "fara TVA")}
                            value={shippingCostExVat}
                            onChange={setShippingCostExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Packing Price", "Pret ambalare")}
                            hint={l("without VAT", "fara TVA")}
                            value={packagingCostExVat}
                            onChange={setPackagingCostExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <NumberField label={l("Target ROAS", "ROAS tinta")} value={targetRoas} onChange={setTargetRoas} />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Selling Price", "Pret vanzare")}
                            hint={l("incl. VAT", "cu TVA")}
                            value={sellingPriceInclVat}
                            onChange={setSellingPriceInclVat}
                        />
                    </GridItem>
                </SimpleGrid>
            </InputsCard>

            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6} mt={{ base: 8, md: 10 }}>
                <KpiStatCard
                    icon={FiDollarSign}
                    label={l("Order Profit", "Profit per comanda")}
                    value={computed.display.orderProfit}
                    accent="teal"
                    statusTag={{
                        colorScheme: computed.profitabilityStatus.color,
                        text: computed.profitabilityStatus.text,
                        icon: FiTrendingUp,
                    }}
                />
                <KpiStatCard icon={FiPercent} label="ROI %" value={computed.display.roi} accent="purple" />
                <KpiStatCard icon={FiTrendingUp} label={l("Profit Margin %", "Marja profit %")} value={computed.display.profitMargin} accent="orange" />
                <KpiStatCard icon={FiTarget} label={l("Break Even Point", "Punct breakeven")} value={computed.display.breakEvenPoint} accent="pink" />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mt={6}>
                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={useColorModeValue("blackAlpha.200", "whiteAlpha.200")}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                    sx={{
                        ".apexcharts-legend.apx-legend-position-right": { top: "0 !important" },
                    }}
                >
                    <HStack mb={3} justify="space-between">
                        <Text fontWeight="800">{l("Cost Structure", "Structura costuri")}</Text>
                    </HStack>

                    <ReactApexChart
                        type="donut"
                        height={260}
                        series={computed.costBreakdownChart.series}
                        options={{
                            labels: computed.costBreakdownChart.labels,
                            colors: ["#3182CE", "#38A169", "#ED8936", "#E53E3E", "#805AD5"],
                            dataLabels: { enabled: false },
                            legend: {
                                position: "right",
                                horizontalAlign: "center",
                                offsetY: 0,
                                markers: { radius: 10 },
                                fontSize: "13px",
                                itemMargin: { horizontal: 12, vertical: 6 },
                                formatter: (seriesName, opts) => {
                                    const val = opts.w.globals.series[opts.seriesIndex];
                                    return `${seriesName} ${val.toFixed(1)}%`;
                                },
                            },
                            tooltip: { y: { formatter: (val) => `${val.toFixed(1)}%` } },
                            stroke: { show: false },
                            plotOptions: {
                                pie: {
                                    expandOnClick: false,
                                    donut: {
                                        size: "70%",
                                        labels: {
                                            show: true,
                                            value: { show: false },
                                            total: {
                                                show: true,
                                                label: l("Costs %", "Costuri %"),
                                                showAlways: true,
                                                formatter: () => "100%",
                                            },
                                        },
                                    },
                                },
                            },
                            states: {
                                hover: { filter: { type: "none" } },
                                active: { filter: { type: "none" } },
                            },
                            responsive: [{ breakpoint: 992, options: { legend: { position: "bottom", offsetY: 0 } } }],
                        }}
                    />
                </Box>

                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={useColorModeValue("blackAlpha.200", "whiteAlpha.200")}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                >
                    <HStack spacing={3} mb={2}>
                        <Icon as={FiTrendingUp} boxSize={5} color="purple.400" />
                        <Badge colorScheme="purple" variant="subtle" borderRadius="full">
                            {l("ROI: Before vs After", "ROI: Inainte vs Dupa")}
                        </Badge>
                    </HStack>

                    <SimpleGrid columns={{ base: 2 }} spacing={4}>
                        <Stat>
                            <StatLabel color="gray.500">{l("Before Returns", "Inainte de retururi")}</StatLabel>
                            <StatNumber fontSize={{ base: "xl", md: "2xl" }}>
                                {computed.display.roiBeforeReturns}
                            </StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel color="gray.500">{l("After Returns", "Dupa retururi")}</StatLabel>
                            <StatNumber fontSize={{ base: "xl", md: "2xl" }}>{computed.display.roiAfterReturns}</StatNumber>
                            <StatHelpText>{l("Impact", "Impact")}: {computed.display.roiImpact}</StatHelpText>
                        </Stat>
                    </SimpleGrid>
                </Box>

                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={useColorModeValue("blackAlpha.200", "whiteAlpha.200")}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                    display="flex"
                    flexDir="column"
                    justifyContent="center"
                >
                    <HStack spacing={3} mb={2}>
                        <Icon as={FiLayers} boxSize={5} color="cyan.400" />
                        <Badge colorScheme="cyan" variant="subtle" borderRadius="full">
                            {l("Cost Per Result", "Cost per rezultat")}
                        </Badge>
                    </HStack>

                    <Stat>
                        <StatLabel color="gray.500">{l("Cost Per Result", "Cost per rezultat")}</StatLabel>
                        <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>{computed.display.costPerResult}</StatNumber>
                    </Stat>
                </Box>
            </SimpleGrid>
        </CalculatorShell>
    );
}

/* =====================================================================================
Profit Calculator
===================================================================================== */
function ProfitCalculator() {
    const { i18n } = useTranslation();
    const l = (en, ro) => (i18n.language === "ro" ? ro : en);
    const [isVatRegistered, setIsVatRegistered] = useState(true);
    const [vatRatePercent, setVatRatePercent] = useState("21");
    const [returnRatePercent, setReturnRatePercent] = useState("0");

    const [shippingCostPerOrderExVat, setShippingCostPerOrderExVat] = useState("0");
    const [packagingCostPerOrderExVat, setPackagingCostPerOrderExVat] = useState("0");

    const [totalRevenueInclVat, setTotalRevenueInclVat] = useState("0");
    const [adSpendTotal, setAdSpendTotal] = useState("0");
    const [productUnitCostExVat, setProductUnitCostExVat] = useState("0");

    const [ordersCount, setOrdersCount] = useState("0");
    const [productsCount, setProductsCount] = useState("0");

    const inputsCardBg = useColorModeValue("whiteAlpha.800", "whiteAlpha.50");
    const inputsCardBorder = useColorModeValue("blackAlpha.200", "whiteAlpha.200");

    const computed = useMemo(() => {
        const vatRate = parseLocaleNumber(vatRatePercent);
        const returnRate = parseLocaleNumber(returnRatePercent);

        const shippingPerOrder = parseLocaleNumber(shippingCostPerOrderExVat);
        const packagingPerOrder = parseLocaleNumber(packagingCostPerOrderExVat);

        const revenueGross = parseLocaleNumber(totalRevenueInclVat);
        const adSpend = parseLocaleNumber(adSpendTotal);
        const unitCost = parseLocaleNumber(productUnitCostExVat);

        const orders = parseLocaleNumber(ordersCount);
        const products = parseLocaleNumber(productsCount);

        const roas = adSpend > 0 ? revenueGross / adSpend : 0;

        const revenueNetAfterReturns = (revenueGross * (100 - returnRate)) / 100;
        const returnedRevenueValue = (revenueGross * returnRate) / 100;

        const vatCollectedOnSales =
            revenueNetAfterReturns - revenueNetAfterReturns / (1 + vatRate / 100);

        const productCostAfterReturns = (products * unitCost * (100 - returnRate)) / 100;
        const shippingCostTotal = orders * shippingPerOrder;
        const packagingCostTotal = orders * packagingPerOrder;

        const vatPaidOnCosts =
            (productCostAfterReturns + shippingCostTotal + packagingCostTotal) * (vatRate / 100);

        const vatNetPayable = isVatRegistered ? vatCollectedOnSales - vatPaidOnCosts : vatPaidOnCosts;

        const profit =
            revenueNetAfterReturns -
            adSpend -
            productCostAfterReturns -
            shippingCostTotal -
            packagingCostTotal -
            vatNetPayable;

        const profitMarginPercent =
            revenueNetAfterReturns > 0 ? (profit / revenueNetAfterReturns) * 100 : 0;

        const averageOrderValue = orders > 0 ? revenueGross / orders : 0;
        const averageProductsPerOrder = orders > 0 ? products / orders : 0;

        const productsKeptAfterReturns = (products * (100 - returnRate)) / 100;

        const totalCosts =
            adSpend + productCostAfterReturns + shippingCostTotal + packagingCostTotal + vatNetPayable;

        const costPerProductSold = productsKeptAfterReturns > 0 ? totalCosts / productsKeptAfterReturns : 0;

        return {
            display: {
                profit: formatRON(profit),
                profitMargin: formatPercent2(profitMarginPercent),
                roas: roas.toFixed(2).replace(".", ","),
                aov: formatRON(averageOrderValue),
                ppo: averageProductsPerOrder.toFixed(2).replace(".", ","),
                cps: formatRON(costPerProductSold),
                returnedValue: formatRON(returnedRevenueValue),
                returnedPct: formatPercent2(returnRate),
            },
        };
    }, [
        isVatRegistered,
        vatRatePercent,
        returnRatePercent,
        shippingCostPerOrderExVat,
        packagingCostPerOrderExVat,
        totalRevenueInclVat,
        adSpendTotal,
        productUnitCostExVat,
        ordersCount,
        productsCount,
    ]);

    return (
        <CalculatorShell>
            <InputsCard surface={inputsCardBg} borderCol={inputsCardBorder}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <GridItem>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0" fontWeight="700">
                                {l("VAT Payer?", "Platitor TVA?")}
                            </FormLabel>
                            <Switch
                                isChecked={isVatRegistered}
                                onChange={(e) => setIsVatRegistered(e.target.checked)}
                                colorScheme="teal"
                                size="lg"
                            />
                        </FormControl>
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Shipping Price", "Pret transport")}
                            hint={l("without VAT", "fara TVA")}
                            value={shippingCostPerOrderExVat}
                            onChange={setShippingCostPerOrderExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Packing Price", "Pret ambalare")}
                            hint={l("without VAT", "fara TVA")}
                            value={packagingCostPerOrderExVat}
                            onChange={setPackagingCostPerOrderExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <NumberField label={l("Return Rate %", "Rata retur %")} value={returnRatePercent} onChange={setReturnRatePercent} />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Total Revenue", "Venit total")}
                            hint={l("incl. VAT", "cu TVA")}
                            value={totalRevenueInclVat}
                            onChange={setTotalRevenueInclVat}
                        />
                    </GridItem>

                    <GridItem>
                        <MoneyField label={l("Ad Spend", "Cheltuiala ads")} value={adSpendTotal} onChange={setAdSpendTotal} />
                    </GridItem>

                    <GridItem>
                        <MoneyField
                            label={l("Product Price", "Pret produs")}
                            hint={l("without VAT", "fara TVA")}
                            value={productUnitCostExVat}
                            onChange={setProductUnitCostExVat}
                        />
                    </GridItem>

                    <GridItem>
                        <NumberField label={l("Orders Count", "Numar comenzi")} value={ordersCount} onChange={setOrdersCount} />
                    </GridItem>

                    <GridItem>
                        <NumberField label={l("Products Count", "Numar produse")} value={productsCount} onChange={setProductsCount} />
                    </GridItem>

                    <GridItem>
                        <NumberField label="VAT %" value={vatRatePercent} onChange={setVatRatePercent} />
                    </GridItem>
                </SimpleGrid>
            </InputsCard>

            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6} mt={{ base: 8, md: 10 }}>
                <KpiStatCard icon={FiDollarSign} label={l("Profit", "Profit")} value={computed.display.profit} accent="teal" />
                <KpiStatCard icon={FiTrendingUp} label={l("Profit Margin", "Marja profit")} value={computed.display.profitMargin} accent="orange" />
                <KpiStatCard icon={FiPercent} label="ROAS" value={computed.display.roas} accent="purple" />
                <KpiStatCard icon={FiGrid} label={l("Avg Products/Order", "Medie produse/comanda")} value={computed.display.ppo} accent="pink" />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mt={6}>
                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={inputsCardBorder}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                >
                    <HStack spacing={3} mb={2}>
                        <Icon as={FiLayers} boxSize={5} color="teal.400" />
                        <Badge colorScheme="teal" variant="subtle" borderRadius="full">
                            {l("Avg Order Value", "Valoare medie comanda")}
                        </Badge>
                    </HStack>
                    <Stat>
                        <StatLabel color="gray.500">{l("Avg Order Value", "Valoare medie comanda")}</StatLabel>
                        <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>{computed.display.aov}</StatNumber>
                    </Stat>
                </Box>

                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={inputsCardBorder}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                >
                    <HStack spacing={3} mb={2}>
                        <Icon as={FiDollarSign} boxSize={5} color="cyan.400" />
                        <Badge colorScheme="cyan" variant="subtle" borderRadius="full">
                            {l("Cost per Product Sold", "Cost per produs vandut")}
                        </Badge>
                    </HStack>
                    <Stat>
                        <StatLabel color="gray.500">{l("CPS (incl. ads & VAT effect)", "CPS (include ads si efect TVA)")}</StatLabel>
                        <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>{computed.display.cps}</StatNumber>
                    </Stat>
                    <Text mt={2} color="gray.500" fontSize="sm">
                        {l("Each product costs you", "Fiecare produs te costa")} {computed.display.cps} {l("including ads.", "incluzand ads.")}
                    </Text>
                </Box>

                <Box
                    p={{ base: 5, md: 6 }}
                    border="1px solid"
                    borderColor={inputsCardBorder}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="2xl"
                    boxShadow="md"
                >
                    <HStack spacing={3} mb={2}>
                        <Icon as={FiPercent} boxSize={5} color="purple.400" />
                        <Badge colorScheme="purple" variant="subtle" borderRadius="full">
                            {l("Total Return Value", "Valoare totala retururi")}
                        </Badge>
                    </HStack>
                    <Stat>
                        <StatLabel color="gray.500">{l("Returned value", "Valoare returnata")}</StatLabel>
                        <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>{computed.display.returnedValue}</StatNumber>
                        <Text mt={1} color="gray.500" fontSize="sm">
                            ({computed.display.returnedPct} {l("of sales", "din vanzari")})
                        </Text>
                    </Stat>
                </Box>
            </SimpleGrid>
        </CalculatorShell>
    );
}

/* ---------------- Shared shells ---------------- */
function CalculatorShell({ children }) {
    return (
        <Box py={{ base: 8, md: 12 }}>
            <Box w="full">{children}</Box>
        </Box>
    );
}

function InputsCard({ children, surface, borderCol }) {
    return (
        <Box
            border="1px solid"
            borderColor={borderCol}
            bg={surface}
            borderRadius="3xl"
            p={{ base: 6, md: 8 }}
            boxShadow="lg"
            backdropFilter="auto"
            backdropBlur="6px"
        >
            {children}
        </Box>
    );
}

/* =====================================================================================
Page with Tabs
===================================================================================== */
export default function CalculatorsPage() {
    const { i18n } = useTranslation();
    const l = (en, ro) => (i18n.language === "ro" ? ro : en);
    const tabBorderColor = useColorModeValue("blackAlpha.200", "whiteAlpha.200");

    return (
        <Box minH="100vh" py={{ base: 6, md: 10 }}>
            <Container maxW={{ base: "100%", md: "7xl" }} px={{ base: 2, md: 8 }}>
                <Tabs variant="soft-rounded" colorScheme="teal" isLazy>
                    <TabList mt={{ base: 24, md: 10 }} p="2" gap="2" overflowX="auto">
                        <Tab border="2px solid" bg="whiteAlpha.600" borderColor={tabBorderColor} fontWeight="700">
                            {l("ROI", "ROI")}
                        </Tab>
                        <Tab border="2px solid" bg="whiteAlpha.600" borderColor={tabBorderColor} fontWeight="700">
                            {l("Profit", "Profit")}
                        </Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel p={0}>
                            <RoiBreakEvenCalculator />
                        </TabPanel>
                        <TabPanel p={0}>
                            <ProfitCalculator />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Container>
        </Box>
    );
}